<template>
  <div class="space-section panel panel-bordered">
    <div class="panel-heading">
      <h3 class="panel-title">Space Quiz</h3>
      <div class="panel-actions panel-actions-keep">
        <button
          type="button"
          class="btn btn-round btn-primary btn-sm"
          :disabled="disableAddButton"
          @click="openCreateModal"
        >
          <i class="icon md-plus" aria-hidden="true"></i>
          Add Question
        </button>
      </div>
    </div>

    <div class="panel-body">
      <loading :active="isLoading" :is-full-page="false" loader="bars" />

      <div v-if="questions.length" class="question-list">
        <QuestionCard
          v-for="(question, index) in questions"
          :key="question.id"
          :index="index"
          :question="question"
          :disabled="disabled"
          @edit="openEditModal(question)"
          @delete="deleteQuestion(question)"
        />
      </div>

      <div v-else class="empty-state">
        No questions yet. Add up to 10 questions for this Space game.
      </div>
    </div>

    <modal :name="modalName" :width="720" height="auto" :clickToClose="false">
      <QuestionModal
        v-if="modalVisible"
        :game-id="gameId"
        :question="selectedQuestion"
        :is-new="isNewQuestion"
        :disabled="disabled"
        @close="handleModalClose"
      />
    </modal>
  </div>
</template>

<script>
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";

import { gameService } from "@/api/client";
import QuestionCard from "@/views/game/space/components/QuestionCard.vue";
import QuestionModal from "@/views/game/space/components/QuestionModal.vue";

export default {
  name: "SpaceQuestionList",

  components: {
    Loading,
    QuestionCard,
    QuestionModal,
  },

  props: {
    gameId: {
      type: [Number, String],
      required: true,
    },
    questions: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isLoading: false,
      modalName: "space-question-modal",
      modalVisible: false,
      selectedQuestion: null,
      isNewQuestion: false,
    };
  },

  computed: {
    disableAddButton() {
      return this.disabled || this.isLoading || this.questions.length >= 10;
    },
  },

  methods: {
    async openCreateModal() {
      if (this.disableAddButton) return;

      const existingIds = this.questions.map((question) => question.id);
      this.isLoading = true;

      try {
        const response = await gameService.createSpaceQuestion(this.gameId);
        let question = null;

        if (response && response.id) {
          question = {
            id: response.id,
            question: "",
            choice1: "",
            choice2: "",
            correct_answer: null,
            extra_time: 0,
          };
        } else {
          const latestGame = await gameService.getById(this.gameId);
          const latestQuestions =
            (latestGame && latestGame.space && latestGame.space.questions) || [];

          question =
            latestQuestions.find((item) => !existingIds.includes(item.id)) ||
            latestQuestions[latestQuestions.length - 1] ||
            null;
        }

        if (!question) {
          throw new Error("Unable to initialize question.");
        }

        this.selectedQuestion = Object.assign({}, question);
        this.isNewQuestion = true;
        this.modalVisible = true;
        this.$modal.show(this.modalName);
      } catch (error) {
        error.response
          ? this.$dialogs.alert(error.response.data)
          : this.$dialogs.alert(error.message || error);
      } finally {
        this.isLoading = false;
      }
    },

    openEditModal(question) {
      if (this.disabled) return;

      this.selectedQuestion = Object.assign({}, question);
      this.isNewQuestion = false;
      this.modalVisible = true;
      this.$modal.show(this.modalName);
    },

    async deleteQuestion(question, silent = false) {
      if (this.disabled && !silent) return;

      if (!silent) {
        const options = { cancelLabel: "No", okLabel: "Yes", size: "sm" };
        const { ok } = await this.$dialogs.confirm(
          `Delete question #${question.id} ?`,
          options
        );

        if (!ok) return;
      }

      this.isLoading = true;

      try {
        await gameService.deleteSpaceQuestion(this.gameId, question.id);
        if (!silent) {
          this.$notify("Delete question success.");
        }
        this.$emit("updated");
      } catch (error) {
        error.response
          ? this.$dialogs.alert(error.response.data)
          : this.$dialogs.alert(error.message || error);
      } finally {
        this.isLoading = false;
      }
    },

    async handleModalClose({ refresh, cleanupQuestionId }) {
      this.modalVisible = false;
      this.$modal.hide(this.modalName);

      if (cleanupQuestionId) {
        await this.deleteQuestion({ id: cleanupQuestionId }, true);
      } else if (refresh) {
        this.$emit("updated");
      }

      this.selectedQuestion = null;
      this.isNewQuestion = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.space-section {
  margin-top: 1.5rem;
}

.question-list {
  gap: 1rem;
  display: grid;
}

.empty-state {
  padding: 1rem;
  text-align: center;
  color: #76838f;
  background: #f8fafc;
  border-radius: 10px;
}
</style>
